import { fg } from '@atlaskit/platform-feature-flags';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/products';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { allowedJiraIssueStatuses } from '../../../../../controllers/filters/selected/utils';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { i18n } from './messages';

const getProductKeys1P = (): ProductKeys1P[] => {
	if (!fg('rovo_search_in_jira_t25_fps_filters')) {
		return [];
	}

	return [ProductKeys.Jira, ProductKeys.JiraProductDiscovery, ProductKeys.JiraServiceManagement];
};

const products3PFromConfig = getProducts3PFromConfigForFilter('statusFilter');

const getAllIssueStatusOptions = (config: CloudConfig) => {
	// Only use allowedJiraIssueStatuses values for Jira issue statuses or else it will be ignored in
	// the GraphQL query request. See the getJiraIssueStatusFilterOptions function.
	const options = allowedJiraIssueStatuses
		.filter((status) => {
			if (!fg('rovo_search_in_jira_granular_status_filters')) {
				return status === 'OPEN' || status === 'DONE';
			} else {
				return status !== 'OPEN';
			}
		})
		.map((issueStatus) => ({
			value: issueStatus,
			trackingKey: issueStatus,
			queryParamValue: issueStatus,
			isDefault: false,
			label: config.intl?.formatMessage(i18n[issueStatus]) ?? '',
			product: config.product,
			isSelected: false,
			isSuggested: false,
		})) satisfies SelectFilterOption[];

	return options;
};

const fetchQueryParams = (queryParams: string[], config: CloudConfig): SelectFilterOption[] => {
	const allOptions = getAllIssueStatusOptions(config);
	return allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);
};

export const filter: Readonly<{
    id: "statusFilter";
    type: "single-select" | "multi-select";
    queryParams: {
        key: string;
    };
    products: ProductKeys[];
    universal: boolean;
    customValueFields?: string[];
    withState: (state: unknown, config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"statusFilter">;
    getInitialState: (config: CloudConfig) => import("../../../../../common/constants/filters/select-filter/types").SelectFilterMonad<"statusFilter">;
    isCompatible: (filterId?: string) => boolean;
    isSupportCustomValue: boolean;
    isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'statusFilter', // This also sets the `id=statusFilter` attribute and unfortunately `id=status` contains a global CSS target
	products: [...getProductKeys1P(), ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'status',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return getAllIssueStatusOptions(config);
		},
	},
});
